import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import * as Yup from "yup";
import { Card } from "antd";
import { Form, Formik } from "formik";
import { includes } from "ramda";

import Layout from "../../components/layout";
import CustomInput from "../../components/customInput";
import CustomLabel from "../../components/customLabel";
import CustomButton from "../../components/customButton";
import PhoneNumberInput from "../../components/phoneInput";
import UserService from "../../service/UserService";
import { setPopup } from "../../actions/popupAction";
import CustomNotification from "../../components/notification";
import { NEXTGEN_EHR_WHITELABEL } from "../../constants/constants";
import MobileHeader from "../../components/header/mobileHeader";
import "./styles.scss";

const UserProfile = (props) => {
  const submitButtonRef = useRef();
  const { customPopupMessage, messageType, message } = props.popupReducer;
  const userService = new UserService();
  const [profileData, setProfileData] = useState({});

  const getProfileData = async () => {
    try {
      const userData = await userService.getProfileData();
      setProfileData(userData);
    } catch (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        props.setPopup({
          customPopupMessage: true,
          messageType: "error",
          message: error.response.data.errors,
        });
      } else {
        props.setPopup({
          customPopupMessage: true,
          messageType: "error",
          message: "Error in fetching details, please refresh and try again!",
        });
      }
    }
  };

  useEffect(() => {
    getProfileData();
  }, []);

  const handleCancel = () => {
    props.history.goBack();
  };

  const handleSubmit = async (values) => {
    if (submitButtonRef && submitButtonRef.current) return;
    submitButtonRef.current = true;
    try {
      values.mobileNo = !includes("+", values.mobileNo)
        ? `+${values.mobileNo}`
        : values.mobileNo;
      const {
        data: { message: newMessage },
      } = await userService.saveProfileData(values);

      setProfileData(values);

      props.setPopup({
        customPopupMessage: true,
        messageType: "success",
        message: newMessage,
      });
    } catch (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        props.setPopup({
          customPopupMessage: true,
          messageType: "error",
          message: error.response.data.errors.message,
        });
      } else if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        props.setPopup({
          customPopupMessage: true,
          messageType: "error",
          message: error.response.data.message,
        });
      } else {
        props.setPopup({
          customPopupMessage: true,
          messageType: "error",
          message: "Sorry, could not update. Please refresh and try again!",
        });
      }
    }
    submitButtonRef.current = false;
  };

  const PhoneRegex = "^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$";
  const alphabetsOnly = /^[a-zA-Z ]+$/;
  // eslint-disable-next-line
  const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const updateProfileSchema = Yup.object().shape({
    name: Yup.string()
      .matches(alphabetsOnly, "Special characters or numbers are not allowed")
      .required("Full Name is required"),
    email: Yup.string()
      .matches(emailRegex, "Please use correct format for Email Address")
      .required("Email is required"),
    mobileNo: Yup.string()
      .required("Phone No is required")
      .matches(PhoneRegex)
      .min(11, "Phone No is too short!"),
  });

  return (
    <Layout testId="user-profile-page-wrapper">
      {customPopupMessage && (
        <CustomNotification messageType={messageType} message={message} />
      )}
      {/* Header for mobile view*/}
      <div className="header-bar-mob">
        <MobileHeader
          showLogo={false}
          showNotifications={false}
          showHelp={false}
          showAccountDetails={false}
          showBackButton={true}
          showPageTitle={true}
          pageTitle="Profile Settings"
          handleBackButton={handleCancel}
          backgroundColor={NEXTGEN_EHR_WHITELABEL ? "black-bg" : ""}
        />
      </div>
      <div className="inner-content-wrapper">
        <Card
          className="primary-card update-profile-card"
          title="Profile Settings"
        >
          <h3 className="heading">Update profile information</h3>

          <Formik
            enableReinitialize
            initialValues={profileData}
            validationSchema={updateProfileSchema}
            onSubmit={(values, _) => {
              handleSubmit(values);
            }}
          >
            {({
              values,
              touched,
              dirty,
              errors,
              isValid,
              setFieldValue,
              setFieldTouched,
            }) => {
              return (
                <Form className="fields-wrapper">
                  <section className="form-row">
                    <CustomInput
                      required
                      title="Full Name"
                      className={"custom-form-input"}
                      placeholder="John Smith"
                      name="name"
                      onBlur={() => setFieldTouched("name", true)}
                    />
                  </section>

                  <section className="form-row">
                    <CustomInput
                      required
                      title="Email Address"
                      className={"custom-form-input"}
                      placeholder="ab.c@xyz.com"
                      name="email"
                      onBlur={() => setFieldTouched("email", true)}
                    />
                  </section>

                  <section className="form-row">
                    <CustomLabel required title="Phone No" />
                    <PhoneNumberInput
                      placeholder="i.e 123456789"
                      containerClass={
                        touched.mobileNo && errors.mobileNo ? "error-box" : ""
                      }
                      name="mobileNo"
                      onChange={(value) => setFieldValue("mobileNo", value)}
                      onBlur={() => setFieldTouched("mobileNo", true)}
                      value={values.mobileNo}
                    />
                    {touched.mobileNo && errors.mobileNo ? (
                      <div className="error-label">{errors.mobileNo}</div>
                    ) : null}
                  </section>

                  <section className="form-actions">
                    <section className="form-buttons">
                      <CustomButton
                        htmlType="submit"
                        type="primary"
                        className={!(isValid && dirty) ? "disable-button" : ""}
                        disabled={!(isValid && dirty)}
                        title="Save Changes"
                      />
                      <CustomButton
                        title="Cancel"
                        buttonType="primary"
                        className="cancel-button ml-8"
                        onClick={handleCancel}
                      />
                    </section>
                  </section>
                </Form>
              );
            }}
          </Formik>
        </Card>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => {
  return {
    popupReducer: state.popupReducer,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setPopup: (data) => dispatch(setPopup(data)),
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(UserProfile)
);
